import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Tag, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { items, total, clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referralDiscount, setReferralDiscount] = useState(0);
  const [checkingReferral, setCheckingReferral] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_country: 'Italia',
    notes: ''
  });

  // Fatturazione aziendale
  const [needsInvoice, setNeedsInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    ragione_sociale: '',
    partita_iva: '',
    codice_sdi: '',
    pec: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleInvoiceChange = (e) => {
    setInvoiceData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const checkReferralCode = async () => {
    if (!referralCode.trim()) return;
    
    setCheckingReferral(true);
    try {
      const response = await axios.post(`${API}/referral/validate?code=${referralCode}`);
      if (response.data.valid) {
        setReferralValid(true);
        setReferralDiscount(total * 0.10);
        toast.success('Codice valido! 10% di sconto applicato');
      }
    } catch (error) {
      setReferralValid(false);
      setReferralDiscount(0);
      toast.error('Codice non valido');
    } finally {
      setCheckingReferral(false);
    }
  };

  const removeReferral = () => {
    setReferralCode('');
    setReferralValid(null);
    setReferralDiscount(0);
  };

  const validateForm = () => {
    if (!formData.customer_name || !formData.customer_email || 
        !formData.shipping_address || !formData.shipping_city || 
        !formData.shipping_postal_code) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    return true;
  };

  const createOrder = async () => {
    if (!validateForm()) return null;
    
    // Valida dati fattura se richiesta
    if (needsInvoice) {
      if (!invoiceData.ragione_sociale || !invoiceData.partita_iva) {
        toast.error('Compila Ragione Sociale e Partita IVA per la fattura');
        return null;
      }
      if (!invoiceData.codice_sdi && !invoiceData.pec) {
        toast.error('Inserisci Codice SDI o PEC per la fattura elettronica');
        return null;
      }
    }
    
    const token = localStorage.getItem('token');
    
    const orderData = {
      items: items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        size: item.selectedSize || null
      })),
      shipping_address: {
        name: formData.customer_name,
        email: formData.customer_email,
        phone: formData.customer_phone,
        address: formData.shipping_address,
        city: formData.shipping_city,
        postal_code: formData.shipping_postal_code,
        country: formData.shipping_country
      },
      payment_method: 'paypal',
      referral_code: referralValid ? referralCode : null,
      notes: formData.notes,
      // Dati fatturazione aziendale
      needs_invoice: needsInvoice,
      invoice_data: needsInvoice ? {
        ragione_sociale: invoiceData.ragione_sociale,
        partita_iva: invoiceData.partita_iva,
        codice_sdi: invoiceData.codice_sdi || null,
        pec: invoiceData.pec || null
      } : null
    };

    // Use guest endpoint if not logged in, otherwise use authenticated endpoint
    if (token) {
      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.order_id;
    } else {
      // Guest checkout
      const response = await axios.post(`${API}/orders/guest`, orderData);
      return response.data.order_id;
    }
  };

  // PayPal checkout
  const handlePayPalCheckout = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }

    setLoading(true);
    try {
      const newOrderId = await createOrder();
      if (!newOrderId) {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/paypal/create-order?order_id=${newOrderId}`,
        {},
        { headers }
      );
      
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('PayPal error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il checkout');
    } finally {
      setLoading(false);
    }
  };

  const shippingCost = total >= 50 ? 0 : 4.99;
  const discountedTotal = total - referralDiscount;
  const finalTotal = discountedTotal + shippingCost;

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="text-3xl md:text-4xl font-bold font-serif mb-8">{t('checkoutTitle')}</h1>

        <form onSubmit={handlePayPalCheckout}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form Fields */}
            <div className="lg:col-span-2 space-y-8">
              {/* Customer Info */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="text-xl font-bold font-serif mb-6">{t('customerInfo')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name">{t('fullName')} *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      data-testid="checkout-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_email">{t('email')} *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      data-testid="checkout-email"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="customer_phone">{t('phone')}</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      data-testid="checkout-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="text-xl font-bold font-serif mb-6">{t('shippingAddress')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="shipping_address">{t('address')} *</Label>
                    <Input
                      id="shipping_address"
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleChange}
                      required
                      data-testid="checkout-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_city">{t('city')} *</Label>
                    <Input
                      id="shipping_city"
                      name="shipping_city"
                      value={formData.shipping_city}
                      onChange={handleChange}
                      required
                      data-testid="checkout-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_postal_code">{t('postalCode')} *</Label>
                    <Input
                      id="shipping_postal_code"
                      name="shipping_postal_code"
                      value={formData.shipping_postal_code}
                      onChange={handleChange}
                      required
                      data-testid="checkout-postal"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="shipping_country">{t('country')}</Label>
                    <Input
                      id="shipping_country"
                      name="shipping_country"
                      value={formData.shipping_country}
                      onChange={handleChange}
                      data-testid="checkout-country"
                    />
                  </div>
                </div>
              </div>

              {/* Order Notes */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="text-xl font-bold font-serif mb-6">{t('orderNotes')}</h2>
                <div className="space-y-2">
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Note speciali per la consegna..."
                    rows={4}
                    data-testid="checkout-notes"
                  />
                </div>
              </div>

              {/* Fatturazione B2B */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="needsInvoice"
                    checked={needsInvoice}
                    onChange={(e) => setNeedsInvoice(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-[#064E3B] focus:ring-[#064E3B]"
                    data-testid="needs-invoice-checkbox"
                  />
                  <Label htmlFor="needsInvoice" className="text-base font-semibold cursor-pointer">
                    Richiedi fattura (per aziende/P.IVA)
                  </Label>
                </div>
                
                {needsInvoice && (
                  <div className="space-y-4 pt-4 border-t border-border animate-in slide-in-from-top-2">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-amber-800">
                        Compila i dati per ricevere la fattura elettronica. Riceverai la fattura via SDI o PEC.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="ragione_sociale">Ragione Sociale *</Label>
                      <Input
                        id="ragione_sociale"
                        name="ragione_sociale"
                        value={invoiceData.ragione_sociale}
                        onChange={handleInvoiceChange}
                        placeholder="Es. Mario Rossi SRL"
                        data-testid="invoice-company"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="partita_iva">Partita IVA *</Label>
                      <Input
                        id="partita_iva"
                        name="partita_iva"
                        value={invoiceData.partita_iva}
                        onChange={handleInvoiceChange}
                        placeholder="Es. IT12345678901"
                        data-testid="invoice-vat"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="codice_sdi">Codice SDI</Label>
                        <Input
                          id="codice_sdi"
                          name="codice_sdi"
                          value={invoiceData.codice_sdi}
                          onChange={handleInvoiceChange}
                          placeholder="Es. M5UXCR1"
                          maxLength={7}
                          data-testid="invoice-sdi"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="pec">oppure PEC</Label>
                        <Input
                          id="pec"
                          name="pec"
                          type="email"
                          value={invoiceData.pec}
                          onChange={handleInvoiceChange}
                          placeholder="Es. azienda@pec.it"
                          data-testid="invoice-pec"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      * Inserisci almeno uno tra Codice SDI e PEC per la fatturazione elettronica
                    </p>
                  </div>
                )}
              </div>

              {/* Referral Code */}
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="text-xl font-bold font-serif mb-4 flex items-center gap-2">
                  <Tag className="h-5 w-5 text-[#064E3B]" />
                  Codice Referral
                </h2>
                {referralValid ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-green-700">{referralCode.toUpperCase()}</span>
                      <span className="text-sm text-green-600">-10% applicato</span>
                    </div>
                    <button onClick={removeReferral} className="text-green-600 hover:text-green-800">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="Inserisci codice"
                      className="flex-1"
                      data-testid="referral-input"
                    />
                    <Button
                      type="button"
                      onClick={checkReferralCode}
                      disabled={checkingReferral || !referralCode.trim()}
                      variant="outline"
                    >
                      {checkingReferral ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Applica'}
                    </Button>
                  </div>
                )}
                {referralValid === false && (
                  <p className="text-sm text-red-500 mt-2">Codice non valido</p>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-border p-6 sticky top-32" data-testid="checkout-summary">
                <h2 className="text-xl font-bold font-serif mb-6">Riepilogo Ordine</h2>

                {/* Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {items.map((item) => (
                    <div key={item.product_id} className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted shrink-0">
                        <img
                          src={item.image || 'https://images.unsplash.com/photo-1592868378269-2c502b2730ff?w=100&h=100&fit=crop'}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium line-clamp-1">{item.title}</p>
                        <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                        <p className="text-sm font-bold">€{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4 space-y-3 mb-6">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t('subtotal')}</span>
                    <span>€{total.toFixed(2)}</span>
                  </div>
                  {referralDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Sconto Referral (-10%)</span>
                      <span>-€{referralDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Spedizione</span>
                    <span>{shippingCost === 0 ? 'Gratis' : `€${shippingCost.toFixed(2)}`}</span>
                  </div>
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between font-bold text-lg">
                      <span>{t('total')}</span>
                      <span className="text-primary" data-testid="checkout-total">
                        €{finalTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* PayPal Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0070BA] hover:bg-[#005ea6] text-white h-12 text-base font-semibold"
                  data-testid="paypal-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.641h6.142c2.06 0 3.545.482 4.413 1.43.844.928 1.073 2.2.685 3.786l-.03.132a.037.037 0 0 0 0 .01c-.69 3.58-2.855 5.402-6.437 5.402H8.25a.771.771 0 0 0-.758.641l-1.12 7.01a.641.641 0 0 1-.633.534l.337-.687z"/>
                      </svg>
                      <span>Paga con PayPal</span>
                      <span className="ml-1">€{finalTotal.toFixed(2)}</span>
                    </div>
                  )}
                </Button>

                {/* PayPal Logo */}
                <div className="flex justify-center mt-4">
                  <img 
                    src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_111x69.jpg" 
                    alt="PayPal" 
                    className="h-8 opacity-80"
                  />
                </div>

                <p className="text-xs text-muted-foreground text-center mt-3">
                  Verrai reindirizzato a PayPal per completare il pagamento in sicurezza.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
